// Las piezas de los dashboards de métricas — ADR-2026-10-06-metricas-de-tienda-online.
//
// La app no tiene librería de gráficos y no hace falta sumar una para esto: son SVG
// chicos con las reglas de la skill de dataviz —marcas finas, grilla de un pelo, el
// color sólo en la marca y nunca en el texto, tooltip al pasar el mouse y vista de
// tabla para quien no lee colores—.
import { useEffect, useMemo, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus, Table2, LineChart } from 'lucide-react';
import { diaCorto, porcentaje, variacion } from './formato';

// ── Medir el ancho ─────────────────────────────────────────────────────────

/** El ancho real del contenedor: el SVG se dibuja a escala 1:1 y el texto no se deforma. */
function useAncho<T extends HTMLElement>() {
  // Ref de callback y no `useRef`: el gráfico se desmonta con "Ver tabla" y vuelve como OTRO
  // elemento con "Ver gráfico". El observador se rearma con cada elemento nuevo; si no, seguía
  // mirando el viejo y el gráfico dejaba de acompañar el ancho de la tarjeta (review #168).
  const [el, setEl] = useState<T | null>(null);
  const [ancho, setAncho] = useState(0);
  useEffect(() => {
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setAncho(Math.floor(e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, ancho] as const;
}

/** Marcas redondas para el eje: 0, 50 mil, 100 mil… */
function marcasDelEje(max: number, cantidad = 4): number[] {
  if (max <= 0) return [0];
  const bruto = max / cantidad;
  const pot = 10 ** Math.floor(Math.log10(bruto));
  const paso = [1, 2, 2.5, 5, 10].map((m) => m * pot).find((p) => p >= bruto) ?? bruto;
  const marcas: number[] = [];
  for (let v = 0; v <= max + paso * 0.001; v += paso) marcas.push(v);
  if (marcas[marcas.length - 1]! < max) marcas.push(marcas[marcas.length - 1]! + paso);
  return marcas;
}

// ── Tarjeta de un número ───────────────────────────────────────────────────

/**
 * Un número con su variación contra el período anterior. La flecha y el color dicen si
 * es bueno: con `menosEsMejor` (reclamos, demoras) bajar es verde.
 */
export function TarjetaKpi({
  etiqueta,
  valor,
  actual,
  anterior,
  contra,
  menosEsMejor = false,
  ayuda,
  serie,
  colorSerie = '#1644EB',
}: {
  etiqueta: string;
  valor: string;
  actual: number;
  anterior: number;
  /** "vs. 30 días anteriores". */
  contra: string;
  menosEsMejor?: boolean;
  ayuda?: string;
  /** Opcional: la tendencia del período, como una línea chica. */
  serie?: number[];
  colorSerie?: string;
}) {
  const v = variacion(actual, anterior);
  const sube = v !== null && v > 0.0005;
  const baja = v !== null && v < -0.0005;
  const bueno = menosEsMejor ? baja : sube;
  const malo = menosEsMejor ? sube : baja;
  return (
    <div className="flex min-w-0 flex-col rounded-2xl border border-neutral-200 bg-surface-card p-4 shadow-sm sm:p-5">
      <p className="text-xs font-medium text-neutral-500" title={ayuda}>
        {etiqueta}
      </p>
      <div className="mt-2 flex items-end justify-between gap-3">
        <p className="truncate text-2xl font-semibold text-brand-black">{valor}</p>
        {serie && serie.length > 1 && <Chispa valores={serie} color={colorSerie} />}
      </div>
      <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-neutral-500">
        {v === null ? (
          <span className="text-neutral-400">Sin datos del período anterior</span>
        ) : (
          <>
            <span
              className={`inline-flex items-center gap-0.5 rounded-full py-0.5 px-1.5 font-medium ${
                bueno ? 'bg-emerald-50 text-emerald-700' : malo ? 'bg-red-50 text-red-700' : 'bg-neutral-100 text-neutral-600'
              }`}
            >
              {sube ? (
                <ArrowUpRight className="h-3 w-3" />
              ) : baja ? (
                <ArrowDownRight className="h-3 w-3" />
              ) : (
                <Minus className="h-3 w-3" />
              )}
              {porcentaje(Math.abs(v))}
            </span>
            {contra}
          </>
        )}
      </p>
    </div>
  );
}

/** La tendencia en 80×28: gris lo anterior, el color en el último tramo. */
function Chispa({ valores, color }: { valores: number[]; color: string }) {
  const w = 80;
  const h = 28;
  const max = Math.max(...valores, 1);
  const min = Math.min(...valores);
  const x = (i: number) => (i / (valores.length - 1)) * (w - 4) + 2;
  const y = (v: number) => h - 3 - ((v - min) / Math.max(max - min, 1)) * (h - 6);
  const d = valores.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const corte = Math.floor(valores.length * 0.75);
  const dFinal = valores
    .slice(corte)
    .map((v, i) => `${i ? 'L' : 'M'}${x(corte + i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden="true">
      <path d={d} fill="none" stroke="#C9CFDB" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <path d={dFinal} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ── Gráfico de líneas por día ──────────────────────────────────────────────

export interface SerieDeLinea {
  id: string;
  nombre: string;
  color: string;
  valores: number[];
}

/**
 * Una o dos líneas por día, con cruz y tooltip al pasar el mouse. Con una sola serie va
 * el área lavada al 10%; con dos, sólo las líneas (dos áreas encimadas ensucian).
 * "Ver tabla" muestra los mismos números en filas: es la salida para quien no
 * distingue los colores, y para el ámbar, que no llega a 3:1 contra el blanco.
 */
export function GraficoDeLineas({
  series,
  fechas,
  formatear,
  formatearEje,
  alto = 240,
}: {
  series: SerieDeLinea[];
  fechas: string[];
  formatear: (n: number) => string;
  formatearEje: (n: number) => string;
  alto?: number;
}) {
  const [ref, ancho] = useAncho<HTMLDivElement>();
  const [indice, setIndice] = useState<number | null>(null);
  const [tabla, setTabla] = useState(false);

  const izq = 64;
  const der = 12;
  const arriba = 12;
  const abajo = 28;
  const n = fechas.length;
  const max = Math.max(...series.flatMap((s) => s.valores), 1);
  const marcas = useMemo(() => marcasDelEje(max), [max]);
  const tope = marcas[marcas.length - 1]!;
  const anchoUtil = Math.max(ancho - izq - der, 1);
  const altoUtil = alto - arriba - abajo;
  const x = (i: number) => izq + (n <= 1 ? anchoUtil / 2 : (i / (n - 1)) * anchoUtil);
  const y = (v: number) => arriba + altoUtil - (v / tope) * altoUtil;
  const pasoX = Math.max(1, Math.round(n / (ancho < 480 ? 3 : 6)));

  const mover = (clientX: number, rect: DOMRect) => {
    const px = clientX - rect.left - izq;
    const i = Math.round((px / anchoUtil) * (n - 1));
    setIndice(Math.min(n - 1, Math.max(0, i)));
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {/* Leyenda siempre con dos series o más: la identidad nunca depende sólo del color. */}
        {series.length > 1 ? (
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-600">
            {series.map((s) => (
              <li key={s.id} className="flex items-center gap-1.5">
                <span className="h-0.5 w-4 rounded-full" style={{ background: s.color }} aria-hidden="true" />
                {s.nombre}
              </li>
            ))}
          </ul>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => setTabla((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-lg py-1 px-2 text-xs font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-brand-black"
        >
          {tabla ? <LineChart className="h-3.5 w-3.5" /> : <Table2 className="h-3.5 w-3.5" />}
          {tabla ? 'Ver gráfico' : 'Ver tabla'}
        </button>
      </div>

      {tabla ? (
        <div className="max-h-[280px] overflow-auto rounded-xl border border-neutral-100">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-neutral-50 text-neutral-500">
              <tr>
                <th className="py-2 px-3 font-medium">Día</th>
                {series.map((s) => (
                  <th key={s.id} className="py-2 px-3 text-right font-medium">
                    {s.nombre}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 tabular-nums text-brand-black">
              {fechas.map((f, i) => (
                <tr key={f}>
                  <td className="py-1.5 px-3 text-neutral-500">{diaCorto(f)}</td>
                  {series.map((s) => (
                    <td key={s.id} className="py-1.5 px-3 text-right">
                      {formatear(s.valores[i] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={ref} className="relative" style={{ height: alto }}>
          {ancho > 0 && (
            <svg
              width={ancho}
              height={alto}
              role="img"
              aria-label={`Gráfico por día: ${series.map((s) => s.nombre).join(' y ')}`}
              onMouseMove={(e) => mover(e.clientX, e.currentTarget.getBoundingClientRect())}
              onMouseLeave={() => setIndice(null)}
            >
              {/* Grilla: líneas de un pelo, enteras, un tono arriba del fondo. */}
              {marcas.map((m) => (
                <g key={m}>
                  <line x1={izq} x2={ancho - der} y1={y(m)} y2={y(m)} stroke="#EEF0F4" strokeWidth={1} />
                  <text x={izq - 10} y={y(m)} dy="0.32em" textAnchor="end" className="fill-neutral-400 text-[11px] tabular-nums">
                    {formatearEje(m)}
                  </text>
                </g>
              ))}
              {fechas.map((f, i) =>
                i % pasoX === 0 || i === n - 1 ? (
                  <text key={f} x={x(i)} y={alto - 8} textAnchor="middle" className="fill-neutral-400 text-[11px]">
                    {diaCorto(f)}
                  </text>
                ) : null,
              )}

              {series.length === 1 && (
                <path
                  d={`M${x(0)},${y(0)} ${series[0]!.valores.map((v, i) => `L${x(i)},${y(v)}`).join(' ')} L${x(n - 1)},${y(0)} Z`}
                  fill={series[0]!.color}
                  opacity={0.1}
                />
              )}
              {series.map((s) => (
                <path
                  key={s.id}
                  d={s.valores.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ')}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}
              {/* El último punto de cada línea, con anillo blanco: dónde termina el período. */}
              {indice === null &&
                series.map((s) => (
                  <circle key={s.id} cx={x(n - 1)} cy={y(s.valores[n - 1] ?? 0)} r={4} fill={s.color} stroke="#fff" strokeWidth={2} />
                ))}

              {indice !== null && (
                <g>
                  <line x1={x(indice)} x2={x(indice)} y1={arriba} y2={arriba + altoUtil} stroke="#C9CFDB" strokeWidth={1} />
                  {series.map((s) => (
                    <circle key={s.id} cx={x(indice)} cy={y(s.valores[indice] ?? 0)} r={4.5} fill={s.color} stroke="#fff" strokeWidth={2} />
                  ))}
                </g>
              )}
            </svg>
          )}

          {indice !== null && (
            <div
              className="pointer-events-none absolute top-2 z-10 min-w-[170px] rounded-xl border border-neutral-200 bg-white p-3 text-xs shadow-lg"
              style={
                x(indice) > ancho / 2
                  ? { right: ancho - x(indice) + 12 }
                  : { left: x(indice) + 12 }
              }
            >
              <p className="font-medium text-brand-black">{diaCorto(fechas[indice]!)}</p>
              <ul className="mt-1.5 space-y-1">
                {series.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-4">
                    <span className="flex items-center gap-1.5 text-neutral-500">
                      <span className="h-2 w-2 rounded-full" style={{ background: s.color }} aria-hidden="true" />
                      {s.nombre}
                    </span>
                    <span className="tabular-nums text-brand-black">{formatear(s.valores[indice] ?? 0)}</span>
                  </li>
                ))}
                {series.length > 1 && (
                  <li className="flex items-center justify-between gap-4 border-t border-neutral-100 pt-1">
                    <span className="text-neutral-500">Total</span>
                    <span className="font-medium tabular-nums text-brand-black">
                      {formatear(series.reduce((t, s) => t + (s.valores[indice] ?? 0), 0))}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Barras horizontales ────────────────────────────────────────────────────

export interface FilaDeBarra {
  id: string;
  nombre: string;
  valor: number;
  /** Lo que se lee a la derecha del valor ("12 u."). */
  detalle?: string;
  color: string;
  /** Puntos chicos al lado del nombre: de qué plataformas viene. */
  marcas?: Array<{ color: string; nombre: string }>;
}

/**
 * Un ranking: barra fina, el valor en la punta y el texto en tinta. Las barras son de
 * UN color por fila según de dónde vienen, nunca más oscuras por ser más grandes: el
 * largo ya dice cuánto.
 */
export function BarrasHorizontales({ filas, formatear }: { filas: FilaDeBarra[]; formatear: (n: number) => string }) {
  const max = Math.max(...filas.map((f) => f.valor), 1);
  return (
    <ol className="space-y-3.5">
      {filas.map((f, i) => (
        <li key={f.id} title={`${f.nombre}: ${formatear(f.valor)}${f.detalle ? ` · ${f.detalle}` : ''}`}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-2">
              <span className="w-4 shrink-0 text-xs tabular-nums text-neutral-400">{i + 1}</span>
              {f.marcas?.map((m) => (
                <span key={m.nombre} title={m.nombre} className="h-2 w-2 shrink-0 rounded-full" style={{ background: m.color }} />
              ))}
              <span className="truncate text-brand-black">{f.nombre}</span>
            </span>
            <span className="shrink-0 tabular-nums text-brand-black">
              {formatear(f.valor)}
              {f.detalle && <span className="ml-1.5 text-xs text-neutral-400">{f.detalle}</span>}
            </span>
          </div>
          <div className="mt-1.5 ml-6 h-2">
            <div
              className="h-full rounded-r-[4px]"
              style={{ width: `${Math.max(2, (f.valor / max) * 100)}%`, background: f.color }}
            />
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── Reparto entre partes ───────────────────────────────────────────────────

/**
 * Cuánto pone cada parte del total, en una barra partida con 2px de aire entre tramos
 * y la leyenda con el número y el porcentaje abajo (los tramos no llevan texto adentro:
 * uno chico lo cortaría).
 */
export function Reparto({
  partes,
  formatear,
}: {
  partes: Array<{ id: string; nombre: string; valor: number; color: string }>;
  formatear: (n: number) => string;
}) {
  const total = partes.reduce((t, p) => t + p.valor, 0) || 1;
  return (
    <div>
      <div className="flex h-3 gap-0.5 overflow-hidden rounded-full">
        {partes.map((p) => (
          <div
            key={p.id}
            title={`${p.nombre}: ${formatear(p.valor)} (${porcentaje(p.valor / total, 0)})`}
            style={{ width: `${(p.valor / total) * 100}%`, background: p.color }}
          />
        ))}
      </div>
      <ul className="mt-4 space-y-2.5">
        {partes.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-2 text-neutral-600">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: p.color }} aria-hidden="true" />
              <span className="truncate">{p.nombre}</span>
            </span>
            <span className="shrink-0 tabular-nums text-brand-black">
              {formatear(p.valor)}
              <span className="ml-2 inline-block w-10 text-right text-xs text-neutral-400">{porcentaje(p.valor / total, 0)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Tarjeta contenedora ────────────────────────────────────────────────────

export function Panel({
  titulo,
  subtitulo,
  accion,
  children,
  className = '',
}: {
  titulo: string;
  subtitulo?: string;
  accion?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`min-w-0 rounded-2xl border border-neutral-200 bg-surface-card p-4 shadow-sm sm:p-5 ${className}`}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-brand-black">{titulo}</h2>
          {subtitulo && <p className="mt-0.5 text-xs text-neutral-500">{subtitulo}</p>}
        </div>
        {accion}
      </div>
      {children}
    </section>
  );
}
